import { useMemo } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { GetProjectMessagesResponseBody } from "@imagine/ai-service/shared-types/route-types";
import { aiServiceFetch } from "@/lib/ai-service-client";
import { HttpError } from "@/lib/http-error";
import { imagineStore } from "@/stores/imagine.store";

type UseProjectMessagesOptions = {
  limit?: number;
  afterMessageId?: string;
  enabled?: boolean;
};

type FetchProjectMessagesOptions = {
  projectId: string;
  limit?: number;
  offset?: number;
  afterMessageId?: string;
};

const DEFAULT_LIMIT = 10;

export async function fetchProjectMessages({
  projectId,
  limit = DEFAULT_LIMIT,
  offset = 0,
  afterMessageId,
}: FetchProjectMessagesOptions): Promise<GetProjectMessagesResponseBody> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  if (afterMessageId) {
    params.set("afterMessageId", afterMessageId);
  }

  const response = await aiServiceFetch(
    `/api/projects/${projectId}/messages?${params.toString()}`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    throw await HttpError.fromResponse(response);
  }

  const data = (await response.json()) as GetProjectMessagesResponseBody;
  return data;
}

interface FetchProjectMessagesAfterOptions {
  projectId: string;
  afterMessageId: string;
  limit?: number;
  offset?: number;
}

export async function fetchProjectMessagesAfter({
  projectId,
  afterMessageId,
  limit = DEFAULT_LIMIT,
  offset = 0,
}: FetchProjectMessagesAfterOptions): Promise<GetProjectMessagesResponseBody> {
  return fetchProjectMessages({ projectId, afterMessageId, limit, offset });
}

export function useProjectMessages({
  limit = DEFAULT_LIMIT,
  afterMessageId,
  enabled = true,
}: UseProjectMessagesOptions = {}) {
  const project = imagineStore.use.project();
  const queryEnabled = Boolean(enabled && project?.id);

  const queryResult = useInfiniteQuery({
    queryKey: ["project-messages", project?.id, limit, afterMessageId],
    enabled: queryEnabled,
    initialPageParam: 0,
    getNextPageParam: (lastPage: GetProjectMessagesResponseBody) => {
      if (!lastPage.pagination.hasMore) {
        return undefined;
      }
      return lastPage.pagination.nextOffset ?? undefined;
    },
    queryFn: async ({ pageParam }) => {
      if (!project?.id) {
        throw new Error("Project ID is required to fetch messages");
      }

      const offset =
        typeof pageParam === "number" && !Number.isNaN(pageParam)
          ? pageParam
          : 0;

      return fetchProjectMessages({
        projectId: project?.id,
        limit,
        offset,
        afterMessageId,
      });
    },
  });

  const flattenedMessages = useMemo(() => {
    if (!queryResult.data?.pages) return [];

    // For WhatsApp-style pagination:
    // - First page (index 0) has NEWEST messages
    // - Subsequent pages have OLDER messages
    // - We want to display oldest->newest, so REVERSE the pages array before flattening
    // - This way older messages (from later pages) appear first in the final array
    const reversedPages = [...queryResult.data.pages].reverse();
    return reversedPages.flatMap((page) => page.messages);
  }, [queryResult.data]);

  const latestPagination = queryResult.data?.pages.at(-1)?.pagination;

  return {
    messages: flattenedMessages,
    pages: queryResult.data?.pages ?? [],
    pagination: latestPagination,
    total: queryResult.data?.pages[0]?.pagination.total ?? 0,
    fetchNextPage: queryResult.fetchNextPage,
    hasNextPage: queryResult.hasNextPage,
    hasMore: queryResult.hasNextPage, // Alias for better semantics
    isFetchingNextPage: queryResult.isFetchingNextPage,
    refetch: queryResult.refetch,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    status: queryResult.status,
    isFinished: queryResult.status === "success",
  };
}
